import useQueryParams from "@shared/hooks/useQueryParams";
import { ExploreFilerParams } from "../types";
import { ActiveFilterType } from "@templates/new-explore/hooks/useExloreStore";
import { EXPLORE_HIDDEN_PARAMS, exploreFilterSchema } from "../schema";
import { ExploreFilterSchemaFields } from "@modules/new-explore/types";
import { useRouter } from "next/router";
import { scrollTop } from "@utils/scroll";
import { useMemo } from "react";
import useExplorePreloadedData from "@templates/new-explore/hooks/useExplorePreloadedData";

export const useExplore = () => {
  const { actions, activeFilters } = useExplorePreloadedData();
  const { setActiveFilters } = actions;
  const { query } = useRouter();
  const {
    clearParams,
    removeParamByValue,
    removeParamByKey,
    setParam,
    setParams,
  } = useQueryParams();

  const clearFilters = async () => {
    clearParams(true, false);
    setActiveFilters(undefined);
  };

  const visibleActiveFilters = useMemo(() => {
    const hiddenKeys = ["bought", "certificate", "count"];
    const copy = { ...activeFilters };
    hiddenKeys.forEach((key) => {
      delete copy[key as ExploreFilterSchemaFields];
    });
    return copy;
  }, [activeFilters]);

  const shouldResetPage = (filterName: ExploreFilerParams) =>
    filterName !== "page" && query?.page && String(query?.page) !== "1";

  const addFilter = (data: {
    filterName: ExploreFilerParams;
    value: string | string[];
    name: string | string[];
    isSingle?: boolean;
    withReplace?: boolean; //replace filter with key
    clear?: boolean; ///clear all value of key
  }) => {
    const {
      filterName,
      value,
      name,
      isSingle = false,
      withReplace = false,
      clear = false,
    } = data;
    scrollTop();

    if (!EXPLORE_HIDDEN_PARAMS.includes(filterName)) {
      const newFilter = {
        filter_label:
          exploreFilterSchema[filterName as ExploreFilterSchemaFields].label,
        filter_value: Array.isArray(value)
          ? (value as string[])?.flatMap((val, index) => ({
              label: name[index],
              id: val,
            }))
          : [
              ...(isSingle
                ? []
                : activeFilters?.[filterName as ExploreFilterSchemaFields]
                    ?.filter_value || []),

              { label: name, id: value },
            ],
      };
      const updatedFilters = {
        ...activeFilters,
        [filterName]: newFilter,
      };
      setActiveFilters(updatedFilters as ActiveFilterType);
    }
    const resetPage = shouldResetPage(filterName);

    setParam({
      key: filterName,
      value: value,
      redirect: !resetPage,
      shallow: false,
      replace: withReplace,
      clear: clear,
    });

    if (resetPage) {
      setParam({
        key: "page",
        value: "1",
        redirect: true,
        shallow: false,
        replace: true,
      });
    }
  };
  const addFilters = (params: {
    data: {
      filterName: ExploreFilerParams;
      value: string | string[];
      name: string | string[];
    }[];
    isSingle: boolean;
    clear?: boolean;
  }) => {
    const { data, isSingle = false, clear = false } = params;
    scrollTop();

    const filtersToApply = [...data];
    const hasNonPageFilter = filtersToApply.some(
      ({ filterName }) => filterName !== "page"
    );

    const firstFilterName = filtersToApply?.[0]?.filterName ?? "";
    if (hasNonPageFilter && shouldResetPage(firstFilterName))
      filtersToApply.push({ filterName: "page", value: "1", name: "" });

    const updatedFilters = filtersToApply.reduce<Partial<ActiveFilterType>>(
      (acc, { filterName, value, name }) => {
        if (EXPLORE_HIDDEN_PARAMS.includes(filterName)) {
          return acc;
        }

        const newFilter = {
          filter_label:
            exploreFilterSchema[filterName as ExploreFilterSchemaFields].label,
          filter_value: Array.isArray(value)
            ? (value as string[])?.flatMap((val, index) => ({
                label: name[index],
                id: val,
              }))
            : [
                ...(isSingle
                  ? []
                  : acc[filterName as ExploreFilterSchemaFields]
                      ?.filter_value || []),
                { label: name as string, id: value as string },
              ],
        };

        return {
          ...acc,
          [filterName]: newFilter,
        };
      },
      {}
    );

    setActiveFilters(
      clear
        ? ({ ...updatedFilters } as ActiveFilterType)
        : { ...activeFilters, ...(updatedFilters as ActiveFilterType) }
    );
    setParams({
      data: filtersToApply.map(({ filterName, value }) => ({
        key: filterName,
        value,
      })),
      redirect: true,
      shallow: false,
      clear: clear,
    });
  };
  const removeFilterByValue = (
    filterName: ExploreFilerParams,
    value: string
  ) => {
    scrollTop();

    const resetPage = shouldResetPage(filterName);
    removeParamByValue(filterName, value, !resetPage, false);

    if (activeFilters) {
      const _activeFilter = Object.entries(activeFilters).reduce(
        (acc, [key, { filter_label, filter_value }]) => {
          acc[key as ExploreFilterSchemaFields] = {
            filter_label: filter_label,
            filter_value:
              key === filterName
                ? filter_value.filter((d) => d.id !== value)
                : filter_value,
          };

          return acc;
        },
        {} as ActiveFilterType
      );
      setActiveFilters(_activeFilter);
    }

    if (resetPage) {
      setParam({
        key: "page",
        value: "1",
        redirect: true,
        shallow: false,
        replace: true,
      });
    }
  };

  const removeFilterByKey = (filterName: ExploreFilerParams) => {
    if (activeFilters) {
      const _activeFilter = Object.fromEntries(
        Object.entries(activeFilters).filter(([key]) => key !== filterName)
      );
      setActiveFilters(_activeFilter as ActiveFilterType);
    }

    const resetPage = shouldResetPage(filterName);
    removeParamByKey(filterName, !resetPage, false);

    if (resetPage) {
      setParam({
        key: "page",
        value: "1",
        redirect: true,
        shallow: false,
        replace: true,
      });
    }
  };

  const getTermLabel = () => {
    if (!query?.term) return "";
    const termString =
      typeof query.term === "string"
        ? query.term
        : Array.isArray(query.term)
          ? query.term[0]
          : "";
    const slicedTerm =
      termString.length < 10 ? termString : `${termString.slice(0, 7)}...`;
    return slicedTerm ? `عبارت جستجو: ${slicedTerm}` : "";
  };

  return {
    visibleActiveFilters,
    clearFilters,
    addFilter,
    removeFilterByKey,
    removeFilterByValue,
    addFilters,
    getTermLabel,
  };
};
